import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

const PRODUCT_CARD_LAYOUT_CONTAINER_SELECTOR = '.product-grid, .resource-list';
const productCardLayoutObservers = new WeakMap();
const productCardLayoutFrames = new WeakMap();

const getProductCardLayoutContainer = (element) => element.closest(PRODUCT_CARD_LAYOUT_CONTAINER_SELECTOR);

const normalizeProductCardContent = (content) => {
  if (!(content instanceof HTMLElement)) return;

  const primaryGroup = content.querySelector(':scope > .group-block:first-of-type');
  if (!(primaryGroup instanceof HTMLElement)) return;

  const groupContent = primaryGroup.querySelector(':scope > .group-block-content');
  if (!(groupContent instanceof HTMLElement)) return;

  const title = groupContent.querySelector(':scope > .text-block:first-of-type');
  const price = groupContent.querySelector(':scope > product-price');

  if (!(title instanceof HTMLElement) || !(price instanceof HTMLElement)) return;
  if (title.parentElement !== groupContent || price.parentElement !== groupContent) return;

  primaryGroup.before(title);
  title.after(price);
};

const getTitleElement = (content) => {
  return (
    content.querySelector(':scope > a.contents > .text-block') ||
    content.querySelector(':scope > .text-block') ||
    content.querySelector(':scope > .group-block:first-of-type .text-block:first-of-type')
  );
};

const getPriceElement = (content) => {
  return content.querySelector(':scope > product-price') || content.querySelector(':scope > .group-block:first-of-type product-price');
};

const getActionsElement = (content) => {
  return content.querySelector(':scope > .product-card-purchase-block') || content.querySelector(':scope > .buy-buttons-block');
};

const resetProductCardMeasurements = ({ title, price, actions }) => {
  if (title) title.style.minHeight = '';
  if (price) price.style.minHeight = '';
  if (actions) actions.style.minHeight = '';
};

const equalizeProductCardLayout = (container) => {
  const cards = [...container.querySelectorAll('product-card')]
    .map((card) => {
      const content = card.querySelector('.product-card__content');
      if (!(content instanceof HTMLElement)) return null;

      normalizeProductCardContent(content);

      return {
        content,
        title: getTitleElement(content),
        price: getPriceElement(content),
        actions: getActionsElement(content),
      };
    })
    .filter(Boolean);

  if (cards.length === 0) return;

  cards.forEach(resetProductCardMeasurements);

  let maxPriceOffset = 0;
  let maxPriceHeight = 0;
  let maxActionsHeight = 0;

  for (const card of cards) {
    const contentRect = card.content.getBoundingClientRect();

    if (card.price instanceof HTMLElement) {
      const priceRect = card.price.getBoundingClientRect();
      maxPriceOffset = Math.max(maxPriceOffset, priceRect.top - contentRect.top);
      maxPriceHeight = Math.max(maxPriceHeight, priceRect.height);
    }

    if (card.actions instanceof HTMLElement) {
      maxActionsHeight = Math.max(maxActionsHeight, card.actions.getBoundingClientRect().height);
    }
  }

  for (const card of cards) {
    if (card.title instanceof HTMLElement && card.price instanceof HTMLElement) {
      const currentOffset = card.price.getBoundingClientRect().top - card.content.getBoundingClientRect().top;
      const offsetDelta = Math.max(0, maxPriceOffset - currentOffset);

      if (offsetDelta > 0.5) {
        card.title.style.minHeight = `${card.title.getBoundingClientRect().height + offsetDelta}px`;
      }
    }

    if (card.price instanceof HTMLElement && maxPriceHeight > 0) {
      card.price.style.minHeight = `${maxPriceHeight}px`;
    }

    if (card.actions instanceof HTMLElement && maxActionsHeight > 0) {
      card.actions.style.minHeight = `${maxActionsHeight}px`;
    }
  }
};

const scheduleProductCardLayout = (container) => {
  if (!(container instanceof HTMLElement)) return;

  const currentFrame = productCardLayoutFrames.get(container);
  if (currentFrame) cancelAnimationFrame(currentFrame);

  const frame = requestAnimationFrame(() => {
    productCardLayoutFrames.delete(container);
    equalizeProductCardLayout(container);
  });

  productCardLayoutFrames.set(container, frame);
};

const observeProductCardLayout = (container) => {
  if (!(container instanceof HTMLElement) || productCardLayoutObservers.has(container)) return;

  const observer = new ResizeObserver(() => {
    scheduleProductCardLayout(container);
  });

  observer.observe(container);
  productCardLayoutObservers.set(container, observer);
};

const refreshAllProductCardLayouts = debounce(() => {
  document.querySelectorAll(PRODUCT_CARD_LAYOUT_CONTAINER_SELECTOR).forEach((container) => {
    scheduleProductCardLayout(container);
  });
}, 100);

window.addEventListener('load', refreshAllProductCardLayouts);
window.addEventListener('resize', refreshAllProductCardLayouts);
document.addEventListener('shopify:section:load', refreshAllProductCardLayouts);

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];
  #layoutObserver = null;
  #layoutTimers = [];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const formVariantId = /** @type {HTMLInputElement | null} */ (this.querySelector('product-form-component input[name="id"]'));

    if (formVariantId?.value) {
      return formVariantId.value;
    }

    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    const content = this.querySelector('.product-card__content');
    if (content instanceof HTMLElement) normalizeProductCardContent(content);

    const layoutContainer = getProductCardLayoutContainer(this);
    observeProductCardLayout(layoutContainer);
    scheduleProductCardLayout(layoutContainer);
    this.#watchLayoutChanges();
    this.#scheduleDeferredLayoutRefreshes();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#layoutObserver?.disconnect();
    this.#layoutObserver = null;
    this.#layoutTimers.forEach((timer) => clearTimeout(timer));
    this.#layoutTimers = [];

    scheduleProductCardLayout(getProductCardLayoutContainer(this));
  }

  #watchLayoutChanges() {
    const elementsToWatch = [this, this.querySelector('.product-card__content'), this.querySelector('.buy-buttons-block')].filter(
      (element) => element instanceof Element
    );

    if (elementsToWatch.length === 0) return;

    this.#layoutObserver?.disconnect();
    this.#layoutObserver = new ResizeObserver(() => {
      scheduleProductCardLayout(getProductCardLayoutContainer(this));
    });

    elementsToWatch.forEach((element) => {
      if (element instanceof Element) this.#layoutObserver?.observe(element);
    });
  }

  #scheduleDeferredLayoutRefreshes() {
    this.#layoutTimers.forEach((timer) => clearTimeout(timer));

    const delays = [150, 500, 1200, 2000];
    this.#layoutTimers = delays.map((delay) =>
      window.setTimeout(() => {
        scheduleProductCardLayout(getProductCardLayoutContainer(this));
      }, delay)
    );
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    const selectedVariantId =
      event.target instanceof HTMLElement && 'selectedOption' in event.target
        ? event.target.selectedOption?.dataset?.variantId || null
        : null;

    this.#syncSelectedVariantState(selectedVariantId || this.getSelectedVariantId());
    this.#syncSelectedVariantAvailability();

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.#syncSelectedVariantState(event.detail.resource?.id ?? null);
    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
    this.#watchLayoutChanges();
    this.#scheduleDeferredLayoutRefreshes();
    scheduleProductCardLayout(getProductCardLayoutContainer(this));
  };

  /**
   * Keeps all card-level product forms and links aligned to the selected variant.
   * This avoids stale variant submissions while asynchronous card re-renders are in flight.
   * @param {string | null} variantId
   */
  #syncSelectedVariantState(variantId) {
    if (!variantId) return;

    this.querySelectorAll('product-form-component input[name="id"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = variantId;
        input.disabled = false;
      }
    });

    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    const links = [productCardLink, productTitleLink, cardGalleryLink].filter((link) => link instanceof HTMLAnchorElement);

    links.forEach((link) => {
      if (!(link instanceof HTMLAnchorElement) || !link.href) return;

      const url = new URL(link.href);
      url.searchParams.set('variant', variantId);
      link.href = url.toString();
    });
  }

  /**
   * Enables or disables add-to-cart controls based on the currently selected radio input.
   */
  #syncSelectedVariantAvailability() {
    const selectedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    if (!selectedInput) return;

    this.#toggleAddToCartButton(selectedInput.dataset.optionAvailable === 'true');
  }

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component') || this.querySelector('variant-picker');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

class CollectionCardVariantSelector extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.#abortController?.abort();
  }

  #handleChange = (event) => {
    if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      if (selectedOption instanceof HTMLOptionElement) {
        this.#applyVariant(selectedOption);
      }
      return;
    }

    if (!(event.target instanceof HTMLInputElement)) return;
    this.#applyVariant(event.target);
  };

  /**
   * @param {HTMLOptionElement | HTMLInputElement} input
   */
  async #applyVariant(input) {
    const productCard = this.closest('product-card');
    if (!(productCard instanceof ProductCard)) return;

    const variantId = input.dataset.variantId;
    const variantUrl = input.dataset.variantUrl;
    const productUrl = (variantUrl || productCard.getProductCardLink()?.href || '').split('?')[0];

    if (!variantId || !productUrl) return;

    this.#setPendingState(productCard, true);

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const requestUrl = new URL(productUrl, window.location.origin);
    requestUrl.searchParams.set('variant', variantId);
    requestUrl.searchParams.set('section_id', 'section-rendering-product-card');

    try {
      const response = await fetch(requestUrl.toString(), { signal: this.#abortController.signal });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const nextCard = html.querySelector('product-card');

      if (!nextCard) return;

      this.#syncCardLinks(productCard, nextCard, html);
      this.#morphCardSection(productCard.querySelector('quick-add-component'), nextCard.querySelector('quick-add-component'));
      this.#morphCardSection(productCard.querySelector('product-price'), nextCard.querySelector('product-price'));
      this.#morphCardSection(
        productCard.querySelector('.product-card-purchase-block'),
        nextCard.querySelector('.product-card-purchase-block')
      );

      productCard.querySelectorAll('product-form-component input[name="id"]').forEach((variantInput) => {
        if (variantInput instanceof HTMLInputElement) {
          variantInput.value = variantId;
          variantInput.disabled = false;
        }
      });

      scheduleProductCardLayout(getProductCardLayoutContainer(productCard));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      this.#setPendingState(productCard, false);
    }
  }

  #setPendingState(productCard, disabled) {
    productCard.toggleAttribute('data-variant-loading', disabled);
  }

  #morphCardSection(currentElement, nextElement) {
    if (!(currentElement instanceof HTMLElement) || !(nextElement instanceof HTMLElement)) return;
    morph(currentElement, nextElement);
  }

  #syncCardLinks(productCard, nextCard, html) {
    const { productCardLink, productTitleLink, cardGalleryLink } = productCard.refs;
    const nextProductCardLink = /** @type {HTMLAnchorElement | null} */ (nextCard.querySelector('[ref="productCardLink"]'));
    const nextProductTitleLink = /** @type {HTMLAnchorElement | null} */ (nextCard.querySelector('[ref="productTitleLink"]'));
    const nextCardGalleryLink = /** @type {HTMLAnchorElement | null} */ (nextCard.querySelector('[ref="cardGalleryLink"]'));

    if (productCardLink instanceof HTMLAnchorElement && nextProductCardLink?.href) {
      productCardLink.href = nextProductCardLink.href;
    }

    if (productTitleLink instanceof HTMLAnchorElement && nextProductTitleLink?.href) {
      productTitleLink.href = nextProductTitleLink.href;
    }

    if (cardGalleryLink instanceof HTMLAnchorElement && nextCardGalleryLink?.href) {
      cardGalleryLink.href = nextCardGalleryLink.href;
    }

    const currentTransitionLink = productCard.closest('product-card-link');
    const nextTransitionLink = html.querySelector('product-card-link');

    if (currentTransitionLink instanceof HTMLElement && nextTransitionLink instanceof HTMLElement) {
      const featuredMediaUrl = nextTransitionLink.getAttribute('data-featured-media-url');

      if (featuredMediaUrl) {
        currentTransitionLink.setAttribute('data-featured-media-url', featuredMediaUrl);
      }
    }
  }
}

if (!customElements.get('collection-card-variant-selector')) {
  customElements.define('collection-card-variant-selector', CollectionCardVariantSelector);
}
